import type { OrderRecord } from '@starkey/shared'
import { ArrowRight, PackageCheck } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { formatDateTime } from '@/lib/dateTime'
import {
  getFulfillmentStatusMeta,
  getPaymentChannelMeta,
  getPaymentStatusMeta,
  getSimpleOrderStatusMeta,
} from '@/lib/orderPresentation'
import { rememberOrderAccess } from '@/lib/orderCache'

export function OrderListItem({ order, timeZone = 'Asia/Shanghai' }: { order: OrderRecord; timeZone?: string }) {
  const navigate = useNavigate()
  const simpleStatus = getSimpleOrderStatusMeta(order)
  const paymentStatus = getPaymentStatusMeta(order.paymentStatus)
  const fulfillmentStatus = getFulfillmentStatusMeta(order.fulfillmentStatus)
  const paymentChannel = getPaymentChannelMeta(order.paymentChannel)

  function openDetail() {
    rememberOrderAccess([order])
    void navigate(`/orders/${order.orderNo}`)
  }

  return (
    <Card
      className="order-list-item order-list-item--interactive"
      onClick={openDetail}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          openDetail()
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="order-list-item__head">
        <div>
          <p className="meta-label">{order.orderNo}</p>
          <h3>
            {order.productName} / {order.variantName}
          </h3>
        </div>
        <Badge className={simpleStatus.className}>{simpleStatus.label}</Badge>
      </div>
      <div className="order-list-item__meta">
        <span>金额 ¥{order.amount.toFixed(2)}</span>
        <span>{formatDateTime(order.createdAt, timeZone)}</span>
      </div>
      <div className="tag-row">
        <Badge className={paymentStatus.className}>{paymentStatus.label}</Badge>
        <Badge className={fulfillmentStatus.className}>{fulfillmentStatus.label}</Badge>
        <Badge className={paymentChannel.className}>{paymentChannel.label}</Badge>
      </div>
      <div className="order-list-item__link-hint">
        <span>点击查看订单详情</span>
        <ArrowRight size={16} />
      </div>
      {order.fulfillmentItems.length > 0 ? (
        <div className="order-list-item__delivery">
          <PackageCheck size={16} />
          已生成 {order.fulfillmentItems.length} 条交付内容
        </div>
      ) : null}
    </Card>
  )
}
